import React, { useState, useEffect } from 'react'
import { Plus, TrendingUp, Calendar, CheckCircle, X, RefreshCw } from 'lucide-react'
import { exchangeRateService, ExchangeRate } from '@/services/exchangeRateService'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'

const ExchangeRateManager: React.FC = () => {
  const { user } = useAuth()
  const [rates, setRates] = useState<ExchangeRate[]>([])
  const [currentRate, setCurrentRate] = useState<ExchangeRate | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [formData, setFormData] = useState({
    rate: '',
    effectiveDate: new Date().toISOString().split('T')[0]
  })

  const canManageRates = user?.role === 'admin' || user?.role === 'hr_manager'

  useEffect(() => {
    loadExchangeRates()
  }, [])

  const loadExchangeRates = async () => {
    try {
      setRefreshing(true)
      const [currentResponse, ratesResponse] = await Promise.all([
        exchangeRateService.getCurrentRate(),
        exchangeRateService.getRates({ limit: 10 })
      ])
      
      setCurrentRate(currentResponse.exchangeRate)
      setRates(ratesResponse.exchangeRates)
    } catch (error) {
      toast.error('Failed to load exchange rates')
    } finally {
      setRefreshing(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canManageRates) return

    setLoading(true)
    try {
      await exchangeRateService.createExchangeRate({
        fromCurrency: 'USD',
        toCurrency: 'CDF',
        rate: parseFloat(formData.rate),
        effectiveDate: formData.effectiveDate
      })

      toast.success('Exchange rate updated successfully')
      setShowForm(false)
      setFormData({
        rate: '',
        effectiveDate: new Date().toISOString().split('T')[0]
      })
      loadExchangeRates()
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update exchange rate')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  if (!canManageRates) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-6">
        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
          <TrendingUp className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">View Only Access</h3>
          <p className="text-gray-600 dark:text-gray-300">You don't have permission to manage exchange rates</p>
        </div>
        
        {/* Display current rate for all users */}
        {currentRate && (
          <div className="mt-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-2">Current Rate</div>
                <div className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                  1 USD = {currentRate.rate.toLocaleString()} CDF
                </div>
                <div className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                  Effective since {formatDate(currentRate.effectiveDate)}
                </div>
              </div>
              <CheckCircle className="h-10 w-10 text-green-500" />
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header avec boutons */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Exchange Rate Management</h2>
          <p className="text-gray-600 dark:text-gray-300 mt-1">Manage USD to CDF conversion rates</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={loadExchangeRates}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 disabled:opacity-50 shadow-lg"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            <Plus className="h-4 w-4" />
            Update Rate
          </button>
        </div>
      </div>

      {/* Current Rate Card */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Current Exchange Rate</h3>

        {currentRate ? (
          <div className="flex items-center gap-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl border border-blue-200 dark:border-blue-800">
            <div className="h-10 w-10 bg-blue-500 rounded-xl flex items-center justify-center shadow-lg">
              <TrendingUp className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold text-blue-900 dark:text-blue-100 mb-2">
                1 USD = {currentRate.rate.toLocaleString()} CDF
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                Effective since {formatDate(currentRate.effectiveDate)}
              </div>
            </div>
            <div className="flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-3 py-2 rounded-lg">
              <CheckCircle className="h-5 w-5" />
              <span className="font-medium text-sm">Active</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600">
            <TrendingUp className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Active Rate</h4>
            <p className="text-gray-600 dark:text-gray-300">Set up the current exchange rate to get started</p>
          </div>
        )}
      </div>

      {/* Rate History */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Recent Exchange Rates</h3>
          <span className="text-sm text-gray-500 dark:text-gray-400">{rates.length} rates</span>
        </div>
        
        <div className="space-y-4">
          {rates.length > 0 ? (
            rates.map((rate) => (
              <div
                key={rate.id}
                className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                  rate.isActive 
                    ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 shadow-md' 
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                }`}
              >
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${
                    rate.isActive 
                      ? 'bg-green-500 text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                  }`}>
                    <Calendar className="h-6 w-6" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900 dark:text-white text-sm">
                      1 USD = {rate.rate.toLocaleString()} CDF
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {formatDate(rate.effectiveDate)}
                    </div>
                  </div>
                </div>
                {rate.isActive && (
                  <span className="px-3 py-1 text-sm bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full font-medium">
                    Active
                  </span>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <p>No exchange rate history available</p>
            </div>
          )}
        </div>
      </div>

      {/* Update Rate Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">Update Exchange Rate</h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Exchange Rate (1 USD = ? CDF)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.rate}
                  onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="e.g., 2900.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Effective Date
                </label>
                <input
                  type="date"
                  required
                  value={formData.effectiveDate}
                  onChange={(e) => setFormData({ ...formData, effectiveDate: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all duration-300 font-medium shadow-lg"
                >
                  {loading ? 'Updating...' : 'Update Rate'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default ExchangeRateManager